import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAddResourceMutation, useDeleteResourceMutation, useGetResourceUrlMutation, useUpdateResourceMutation } from "../../api/vmsSlice";
import { useResource, useUserDetail, useUserPermissions } from "../../services/hooks";
import UploadFileButton, { ActionMenu, Button, ConfirmationDialog, Dialog, GenericTable, Input, Loader, tCell, toastMessage } from "../utils";
import theme, { buttonClasses } from "../utils/theme";
import { EyeIcon } from "../../services/icons";

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

const Resources = () => {
	const updateDataRef = useRef(null);
	const [isModal, setIsModal] = useState(false);
	const [isViewerModal, setIsViewerModal] = useState(false);
	const [currentResourceFile, setCurrentResourceFile] = useState(null);
	const { userPermissions, isFetched } = useUserPermissions();
	const { isSuperAdmin } = useUserDetail();
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [resourceToDelete, setResourceToDelete] = useState(false);
	const [selectedResource, setSelectedResource] = useState(null);
	const [handleDelete] = useDeleteResourceMutation();
	const [getResourceUrl, { isLoading: isFileLoading }] = useGetResourceUrlMutation();

	const onModalOpen = () => setIsModal(true);
	const onModalClose = () => {
		setIsModal(false);
		if (updateDataRef.current) {
			updateDataRef.current = null;
		}
	};

	const closeViewerModal = () => {
		setIsViewerModal(false);
		setCurrentResourceFile(null);
	};

	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;
	const { resources, getResourceDetails, isLoading, count } = useResource(filterRef.current);

	const header = ["S.no", "Name", "Description", "Type", "Resource"];

	if (isSuperAdmin || userPermissions?.updateResource?.value || userPermissions?.deleteResource?.value) {
		header.push("Actions");
	}

	const actionHandlers = {};
	if (isSuperAdmin || userPermissions?.updateResource?.value) {
		actionHandlers.edit = (id) => {
			updateDataRef.current = resources[id];
			setIsModal(true);
		};
	}
	if (isSuperAdmin || userPermissions?.deleteResource?.value) {
		actionHandlers.delete = (id) => {
			setDeleteConfirmOpen(true);
			setResourceToDelete(resources[id]);
		};
	}
	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false);
		setResourceToDelete(null);
	};

	const handleDeleteConfirm = async () => {
		if (!resourceToDelete) return;

		const payload = { id: resourceToDelete._id };
		const {
			data: { error, message },
		} = await handleDelete(payload);

		if (error) {
			toastMessage(false, message);
		} else {
			onUpdateSuccess();
			toastMessage(true, message);
		}
		setDeleteConfirmOpen(false);
		setResourceToDelete(null);
	};

	const handleResourceClick = async (d) => {
		try {
			const response = await getResourceUrl({ fileKey: `resources/${d?.fileName}` });
			let url = response?.data?.data;

			if (!url) return;

			if (d.fileName.endsWith(".pdf")) {
				const newWindow = window.open(url, "_blank");
				if (newWindow) {
					newWindow.document.title = d?.title || "PDF Resource";
				}
			} else {
				setSelectedResource(d);
				setCurrentResourceFile({ url: url, type: "video" });
				setIsViewerModal(true);
			}
		} catch (error) {
			console.error("Failed to fetch file:", error);
		}
	};

	const rows = resources.map((d, i) => {
		let row = [];
		row.push(tCell((page_no - 1) * pagesize + i + 1));
		row.push(tCell(d?.title || "-"));
		row.push(tCell(d?.description || "-"));
		row.push(tCell(d?.fileName?.split(".")?.pop()) || "-");
		row.push(tCell(d.fileName ? <EyeIcon /> : "-", theme.themeOrange, "pointer", () => handleResourceClick(d), "video"));

		if (isSuperAdmin || userPermissions?.updateResource?.value || userPermissions?.deleteResource?.value) {
			row.push(<ActionMenu id={i} handlers={actionHandlers} />);
		}
		return row;
	});
	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getResourceDetails(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value };
		getResourceDetails(filterRef.current);
	};
	const onUpdateSuccess = () => {
		getResourceDetails(filterRef.current);
	};
	return (
		<>
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">Resources</h1>
				</div>
				{isSuperAdmin || userPermissions?.addResource?.value ? (
					<div className="cust-col-5 flex-jc-end">
						<Link className="a_tag_no">
							<Button onClick={onModalOpen} text="Upload Resource" style={buttonClasses.lynkitOrangeEmpty} />
						</Link>
					</div>
				) : null}
			</div>
			<hr className="bgg" />
			<div className="main_content">
				<GenericTable
					header={header}
					rows={rows}
					pageCount={count}
					pageNo={filterRef.current?.page_no}
					limit={filterRef.current?.pagesize}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					isLoading={isLoading}
				/>
			</div>
			{isModal && <AddResources open={isModal} onClose={onModalClose} updateData={updateDataRef.current} onUpdateSuccess={onUpdateSuccess} />}
			{isViewerModal && <ResourceViewer currentResourceFile={currentResourceFile} resource={selectedResource} open={isViewerModal} onClose={closeViewerModal} isFileLoading={isFileLoading} />}
			<ConfirmationDialog open={deleteConfirmOpen} handleClose={handleDeleteCancel} handleSubmit={handleDeleteConfirm} title="Delete Resource" submitText="Delete">
				Are you sure you want to delete this resource? This action cannot be undone.
			</ConfirmationDialog>
		</>
	);
};

const defaultForm = {
	title: "",
	description: "",
	file: null,
	filePath: "",
	fileName: "",
};

function fillUpdateData(data) {
	return {
		title: data.title,
		description: data.description,
		filePath: data.filePath,
		fileName: data.fileName,
	};
}

export const AddResources = ({ onClose, open, updateData, onUpdateSuccess }) => {
	const [formData, setFormData] = useState(updateData ? fillUpdateData(updateData) : defaultForm);
	const [error, setError] = useState({});
	const [handleAdd, { isLoading: isAdding }] = useAddResourceMutation();
	const [handleUpdate] = useUpdateResourceMutation();

	const handleClose = () => {
		onClose();
	};

	const handleInput = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleFileChange = (file) => {
		if (file) {
			const fileExtension = file.name.split(".").pop().toLowerCase();
			const validExtensions = ["pdf", "mp4", "mov", "avi", "wmv", "webm"];

			if (!validExtensions.includes(fileExtension)) {
				alert("Please upload only PDF or video files (MP4, MOV, AVI, WMV, WEBM, PDF)");
				return;
			}
			setFormData({ ...formData, file: file, fileName: file.name });
		} else {
			// clear both file and fileName
			setFormData({ ...formData, file: null, fileName: "" });
		}
	};

	const handleFormValidation = () => {
		let error = {};
		if (!formData.title) error.title = "Title is required";
		if (!updateData && !formData.file) error.file = "File is required";
		if (Object.keys(error).length) {
			setError(error);
		} else {
			handleUpload();
		}
	};
	const handleUpload = async () => {
		const _formData = new FormData();
		_formData.append("file", formData.file);
		_formData.append("title", formData.title);
		_formData.append("description", formData.description);
		if (updateData) {
			_formData.append("id", updateData._id);
		}
		const {
			data: { error, message },
		} = updateData ? await handleUpdate(_formData) : await handleAdd(_formData);

		if (error) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			onUpdateSuccess();
			onClose();
		}
	};

	return (
		<Dialog size="xs" open={open} handleClose={handleClose} handleSubmit={handleFormValidation} title={"Upload Resource"}>
			<div className="form-inputs mt-2">
				<Input label="Title" placeholder="Enter title" name="title" value={formData.title} onChange={handleInput} required error={error.title} />
				<Input label="Description" placeholder="Enter description" name="description" value={formData.description} onChange={handleInput} />
				<UploadFileButton
					allowedExtensions=".pdf, .mp4, .mov, .avi, .wmv, .webm"
					name="file"
					fileName={formData.fileName}
					onChange={handleFileChange}
					onChangeClear={() => handleFileChange(null)}
					id="uploadVideo"
					buttonText="Upload File (pdf/video)"
					required
					error={error.file}
				/>
			</div>
		</Dialog>
	);
};

export const ResourceViewer = ({ open, onClose, currentResourceFile, resource, isFileLoading }) => {
	const [isFullscreen, setIsFullscreen] = useState(false);
	const viewerRef = useRef(null);

	const handleClose = () => {
		if (isFullscreen) document.exitFullscreen?.();
		onClose();
	};

	const resourceType = currentResourceFile?.type;
	const fileUrl = currentResourceFile?.url || currentResourceFile;
	return (
		<Dialog size="lg" open={open} handleClose={handleClose} title={resource?.title || "Resource Viewer"} noBtn={true}>
			<div ref={viewerRef} className={`resource-viewer ${isFullscreen ? "fullscreen" : ""}`} style={{ width: "100%", height: isFullscreen ? "100%" : "400px" }}>
				{isFileLoading ? (
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
						<Loader size="2rem" />
					</div>
				) : // : resourceType === "pdf" ? (
				// 	<iframe src={currentResourceFile} width="100%" height="100%" frameBorder="0" title={resource?.title}  type="application/pdf"/>
				// )
				resourceType === "video" ? (
					<video controls style={{ width: "100%", height: "100%" }} autoPlay>
						<source src={fileUrl} type="video/mp4" />
					</video>
				) : (
					<div className="flex-center" style={{ height: "100%" }}>
						No resource available
					</div>
				)}
			</div>
		</Dialog>
	);
};

export default Resources;
